"use client";

import React, { useEffect, useRef } from "react";
import styles from "./TokensPageTextBlock.module.scss";

export const TokensPageTextBlock: React.FC = () => {
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let video: HTMLVideoElement | null = null;
    let hasLoaded = false;
    let scrollListener: (() => void) | null = null;

    function loadAndPlayVideo() {
      if (hasLoaded || !video) return;
      hasLoaded = true;
      // Set sources dynamically
      const source1080 = document.createElement('source');
      source1080.src = '/tkn_lnd_pg_assets/videos/card/card-hero-1080.mp4';
      source1080.type = 'video/mp4';
      source1080.media = '(min-width: 800px)';
      const source480 = document.createElement('source');
      source480.src = '/tkn_lnd_pg_assets/videos/card/card-hero-480.mp4';
      source480.type = 'video/mp4';
      video.appendChild(source1080);
      video.appendChild(source480);
      video.load();
      video.play().catch(() => {});
    }

    function onScroll() {
      if (!video) return;
      const rect = video.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        loadAndPlayVideo();
        window.removeEventListener('scroll', onScroll);
      }
    }

    if (videoWrapperRef.current) {
      video = videoWrapperRef.current.querySelector('video');
      if (video) {
        // Remove only <source> children to prevent preload, but keep <track> for accessibility
        Array.from(video.querySelectorAll('source')).forEach(source => video && video.removeChild(source));
        video.poster = '/tkn_lnd_pg_assets/videos/card/card-hero-poster-700x0.webp';
        video.preload = 'none';
        scrollListener = onScroll;
        window.addEventListener('scroll', onScroll);
      }
    }
    return () => {
      if (scrollListener) window.removeEventListener('scroll', scrollListener);
    };
  }, []); 

  return (
    <section className={styles.textBlockWrapper}>
      <div className={styles.textBlockSection}>
        <h2 className="text-xl font-semibold mb-4">Explore Live Solana Token Prices &amp; On-Chain Market Data</h2>
        <div className="text-base leading-relaxed">
          <p>Welcome to the ultimate Solana token price tracker — your go-to destination for real-time prices, market data, and trends across the Solana blockchain. Whether you&apos;re an investor, trader, or just exploring the Solana ecosystem, this page offers a comprehensive view of the most active and valuable SPL tokens.</p>
          <p className="mt-4">Get up-to-date information on:</p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Current token prices (live updates)</li>
            <li>Market capitalization and fully diluted valuation (FDV)</li>
            <li>Circulating and total supply</li>
            <li>24h trading volume and liquidity</li>
            <li>Top gainers, losers, and trending tokens</li>
          </ul>
          <p>From popular memecoins like WIF, BONK, and POPCAT, to essential DeFi tokens such as JUP, MNT, and RAY, discover a diverse range of projects building on Solana. Each <a href="/tkn_lnd_pg_sitemap/token-index/">token listed</a> includes transparent on-chain data, helping you analyze performance and track price action with confidence.</p>
          <p className="mt-4">This Solana token dashboard is powered by real-time blockchain data and regularly refreshed, so you never miss a market move. Compare tokens, view charts, and explore historical trends directly on Solana — known for its speed, scalability, and ultra-low transaction fees.</p>
          <p className="mt-4">Whether you&apos;re looking for early-stage gems or monitoring the largest Solana-based tokens, you&apos;re in the right place.</p>
        </div>
      </div>
      <div className={styles.textBlockSectionWhite + ' ' + styles.videoBlock} ref={videoWrapperRef}>
        <h3 className={styles.cardTitle}>Spend smarter with Solflare.</h3>
        <p className={styles.cardText}>Introducing the Solflare Card — the first true self-custody debit card on Solana. Spend your $USDC directly from your wallet, anytime, anywhere — no banks, no borders, no hidden fees. Your funds stay in your control, while you stay ahead. Join the waitlist today and be among the first to spend crypto the Web3 way.</p>
        <video
          className={"c-responsive-media__video js-video-on-scroll-video " + styles.videoBlockVideo}
          poster="/tkn_lnd_pg_assets/videos/card/card-hero-poster-700x0.webp"
          muted
          loop
          playsInline
          preload="none"
          aria-label="Solflare Card background animation"
        >
          <track kind="captions" label="No audio" />
        </video>
        <div className={styles.cardButtonRow}>
          <a href="https://waitlist.solflare.com/" className={styles.cardButtonPrimary}>Join the waitlist</a>
          <a href="https://www.solflare.com/crypto-card/#steps" className={styles.cardButtonSecondary}>How the Solflare Card Works</a>
        </div>
      </div>
      
      {/* Hidden link for search engines - Token Index */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <a href="/tkn_lnd_pg_sitemap/token-index/">Token Index - Browse all tokens alphabetically</a>
      </div>
    </section>
  );
};

export function StocksPageTextBlock() {
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let video: HTMLVideoElement | null = null;
    let hasLoaded = false;
    let scrollListener: (() => void) | null = null;

    function loadAndPlayVideo() {
      if (hasLoaded || !video) return;
      hasLoaded = true;
      // Set sources dynamically
      const source1080 = document.createElement('source');
      source1080.src = '/tkn_lnd_pg_assets/videos/card/card-hero-1080.mp4';
      source1080.type = 'video/mp4';
      source1080.media = '(min-width: 800px)';
      const source480 = document.createElement('source');
      source480.src = '/tkn_lnd_pg_assets/videos/card/card-hero-480.mp4';
      source480.type = 'video/mp4';
      video.appendChild(source1080);
      video.appendChild(source480);
      video.load();
      video.play().catch(() => {});
    }

    function onScroll() {
      if (!video) return;
      const rect = video.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        loadAndPlayVideo();
        window.removeEventListener('scroll', onScroll);
      }
    }

    if (videoWrapperRef.current) {
      video = videoWrapperRef.current.querySelector('video');
      if (video) {
        // Remove only <source> children to prevent preload, but keep <track> for accessibility
        Array.from(video.querySelectorAll('source')).forEach(source => video && video.removeChild(source));
        video.poster = '/tkn_lnd_pg_assets/videos/card/card-hero-poster-700x0.webp';
        video.preload = 'none';
        scrollListener = onScroll;
        window.addEventListener('scroll', onScroll);
      }
    }
    return () => {
      if (scrollListener) window.removeEventListener('scroll', scrollListener);
    };
  }, []);

  return (
    <section className={styles.textBlockWrapper}>
      <div className={styles.textBlockSection}>
      <h2 className="u-bold-07 font-semibold mb-8">Why tokenized stocks?</h2>
      <p className="text-base leading-relaxed">Tokenized stocks (xStocks) are blockchain-based representations of real-world equities. They offer the benefits of crypto—speed, transparency, and accessibility, while giving you exposure to traditional financial markets.</p>
      <p className="text-base leading-relaxed mb-12">This is the future of investing: fully decentralized, 24/7, and available to anyone with a wallet.</p>

        <h2 className="u-bold-07 font-semibold mb-8">What You Can Do With Solflare xStocks</h2>
        <div className="text-base leading-relaxed">
          <h3 className="u-bold-10 mb-1">Buy real-world stocks with crypto</h3>
          <p className="mb-4">Use USDC or SOL to swap into tokenized stocks instantly.</p>

          <h3 className="u-bold-10 mb-1">Trade 24/7. Anywhere.</h3>
          <p className="mb-4">No waiting for market open. xStocks trade 24/7 on-chain.</p>

          <h3 className="u-bold-10 mb-1">No KYC or credit checks</h3>
          <p className="mb-4">Get started with just your <a href="https://www.solflare.com/guides/how-to-set-up-your-first-crypto-wallet-with-solflare-step-by-step/">Solflare wallet</a>. Your identity stays yours.</p>

          <h3 className="u-bold-10 mb-1">Own your assets, always</h3>
          <p className="mb-4">With Solflare, you hold your tokenized stocks in full self-custody.</p>

          <h3 className="u-bold-10 mb-1">Invest any amount</h3>
          <p className="mb-4">Buy fractional shares—no minimums or restrictions.</p>

          <h3 className="u-bold-10 mb-1">Track everything on-chain</h3>
          <p className="mb-4">Every trade, transfer, and price update is publicly verifiable on Solana.</p>

          <h3 className="u-bold-10 mb-1">Key Features at a Glance</h3>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Real-time price tracking with live performance charts</li>
            <li>Live 24h volume & liquidity data</li>
            <li>Market capitalization insights and price changes</li>
            <li>Top gainers and trending xStocks</li>
            <li>1-click swap from <a href="https://www.solflare.com/crypto-card/">USDC or SOL</a> to xStocks</li>
          </ul>
        </div>
      </div>
      <div className={styles.textBlockSectionWhite + ' ' + styles.videoBlock} ref={videoWrapperRef}>
        <h3 className={styles.cardTitle}>Spend smarter with Solflare.</h3>
        <p className={styles.cardText}>Introducing the Solflare Card — the first true self-custody debit card on Solana. Spend your $USDC directly from your wallet, anytime, anywhere — no banks, no borders, no hidden fees. Your funds stay in your control, while you stay ahead. Join the waitlist today and be among the first to spend crypto the Web3 way.</p>
        <video
          className={"c-responsive-media__video js-video-on-scroll-video " + styles.videoBlockVideo}
          poster="/tkn_lnd_pg_assets/videos/card/card-hero-poster-700x0.webp"
          muted
          loop
          playsInline
          preload="none"
          aria-label="Solflare Card background animation"
        >
          <track kind="captions" label="No audio" />
        </video>
        <div className={styles.cardButtonRow}>
          <a href="https://waitlist.solflare.com/" className={styles.cardButtonPrimary}>Join the waitlist</a>
          <a href="https://www.solflare.com/crypto-card/#steps" className={styles.cardButtonSecondary}>How the Solflare Card Works</a>
        </div>
      </div>
    </section>
  );
}
